/**
 * Unpositioned Queue Component
 * Horizontal row of workshops that haven't been positioned yet
 */

import React, { useState, useRef, useCallback } from 'react';
import { WorkshopBasic, plantMappingUtils } from '../../services/plantMapping';
import { usePlantMappingStore } from '../../stores/plantMappingStore';

interface UnpositionedQueueProps {
  workshops: WorkshopBasic[];
  canEditLayout?: boolean;
  onWorkshopHover: (workshop: WorkshopBasic, event: React.MouseEvent) => void;
  onWorkshopLeave: () => void;
  onWorkshopClick: (workshop: WorkshopBasic) => void;
}

const UnpositionedQueue: React.FC<UnpositionedQueueProps> = ({
  workshops,
  canEditLayout = false,
  onWorkshopHover,
  onWorkshopLeave,
  onWorkshopClick
}) => {
  const [draggedWorkshop, setDraggedWorkshop] = useState<WorkshopBasic | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const queueRef = useRef<HTMLDivElement>(null);

  const { createMissingPositions, updatePosition } = usePlantMappingStore();

  // Handle drag start from queue
  const handleDragStart = useCallback((workshop: WorkshopBasic, event: React.MouseEvent) => {
    if (!canEditLayout) return;
    event.preventDefault();
    setDraggedWorkshop(workshop);
    
    const rect = event.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: event.clientX - rect.left - rect.width / 2,
      y: event.clientY - rect.top - rect.height / 2
    });
  }, [canEditLayout]);

  // Handle drag end - create position if dropped on canvas
  const handleDragEnd = useCallback(async (event: MouseEvent) => {
    if (!draggedWorkshop) return;

    // Check if dropped on plant canvas
    const plantCanvas = document.querySelector('.plant-content');
    const canvasRect = plantCanvas?.getBoundingClientRect();
    
    if (canvasRect && 
        event.clientX >= canvasRect.left && 
        event.clientX <= canvasRect.right &&
        event.clientY >= canvasRect.top && 
        event.clientY <= canvasRect.bottom) {
      
      // Convert drop position to normalized coordinates
      const relativeX = (event.clientX - canvasRect.left) / canvasRect.width;
      const relativeY = (event.clientY - canvasRect.top) / canvasRect.height;
      
      // Clamp to bounds
      const normalizedX = Math.max(0, Math.min(1, relativeX));
      const normalizedY = Math.max(0, Math.min(1, relativeY));

      try {
        // First ensure the workshop has a position record
        await createMissingPositions();
        
        // Get the current state to find the newly created position
        const { positions } = usePlantMappingStore.getState();
        const workshopPosition = positions.find(pos => pos.workshop.id === draggedWorkshop.id);
        
        if (workshopPosition) {
          // Update the position with the drop coordinates
          await updatePosition(workshopPosition.id, normalizedX, normalizedY);
          console.log(`Workshop ${draggedWorkshop.workshop_number} positioned at (${normalizedX.toFixed(3)}, ${normalizedY.toFixed(3)})`);
        } else {
          console.error('Could not find position record for workshop:', draggedWorkshop.id);
          // The position should be created by createMissingPositions, but if not found,
          // let's try again after a short delay
          setTimeout(async () => {
            const { positions } = usePlantMappingStore.getState();
            const retryPosition = positions.find(pos => pos.workshop.id === draggedWorkshop.id);
            if (retryPosition) {
              await updatePosition(retryPosition.id, normalizedX, normalizedY);
            }
          }, 100);
        }
      } catch (error) {
        console.error('Failed to position workshop:', error);
        // Don't refresh automatically on errors - let user retry
      }
    }

    setDraggedWorkshop(null);
  }, [draggedWorkshop, createMissingPositions, updatePosition]);

  // Global drag handling
  React.useEffect(() => {
    if (draggedWorkshop) {
      const handleMouseMove = (event: MouseEvent) => {
        // Update drag preview position if needed
      };

      const handleMouseUp = (event: MouseEvent) => {
        handleDragEnd(event);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [draggedWorkshop, handleDragEnd]);

  if (workshops.length === 0) {
    return null;
  }

  return (
    <div className="absolute top-0 left-0 right-0 h-20 bg-white border-b shadow-sm z-10">
      <div className="flex items-center h-full px-4">
        <div className="flex items-center gap-2 text-sm text-gray-600 mr-4">
          <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
          <span>Unpositioned Workshops ({workshops.length})</span>
        </div>
        
        <div 
          ref={queueRef}
          className="flex items-center gap-3 overflow-x-auto flex-1 py-2"
        >
          {workshops.map((workshop) => {
            const categoryColor = plantMappingUtils.getSQDCMCategoryColor(workshop.sqdcm_category);
            const statusColor = plantMappingUtils.getWorkshopStatusColor(workshop.status);
            
            return (
              <div
                key={workshop.id}
                className={`
                  flex-shrink-0 relative group ${canEditLayout ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}
                  ${draggedWorkshop?.id === workshop.id ? 'opacity-50' : ''}
                `}
                onMouseDown={canEditLayout ? (e) => handleDragStart(workshop, e) : undefined}
                onMouseEnter={(e) => onWorkshopHover(workshop, e)}
                onMouseLeave={onWorkshopLeave}
                onClick={() => onWorkshopClick(workshop)}
              >
                {/* Workshop Bubble */}
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm border-2 transition-transform group-hover:scale-110"
                  style={{
                    backgroundColor: categoryColor,
                    borderColor: statusColor
                  }}
                >
                  {workshop.workshop_number}
                </div>
                
                {/* Status Indicator */}
                <div
                  className="absolute -top-1 -right-1 w-4 h-4 rounded-full border-2 border-white"
                  style={{ backgroundColor: statusColor }}
                ></div>
                
                {/* Workshop Title (on hover) */}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20">
                  {workshop.title}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Instructions */}
        {canEditLayout && (
        <div className="text-xs text-gray-500 ml-4 whitespace-nowrap">
          Drag workshops to position them on the plant layout
        </div>
        )}
      </div>
    </div>
  );
};

export default UnpositionedQueue;
